import React, { useState, useEffect } from "react";
import MainCalculatorsList from "../FoodCalculator/MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "محول الغرام إلى كوب - تحويل دقيق للمكونات في الطبخ والخبز";
const DESCRIPTION = "محول سريع ودقيق لتحويل الغرام إلى كوب والعكس. مثالي للدقيق، السكر، الزبدة، والسوائل. أدخل الكمية بالغرام واعرف كم كوب تحتاج لمطبخك.";
const KEYWORDS = "تحويل الغرام إلى كوب, محول المقادير, حساب كوب الدقيق بالغرام, تحويل وحدات الطبخ, grams to cups converter, cooking measurement converter";
const OG_URL = "https://do-calculate.com/calculator/ar/grams-to-cups/";

// JSON-LD Schema for SEO
const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "محول الغرام إلى كوب",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "الرئيسية",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "الحاسبات",
        item: "https://do-calculate.com/ar/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "محول الغرام إلى كوب",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "كم كوب يساوي 250 غرام من الدقيق؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "250 غرام من الدقيق العادي تعادل تقريباً كوبين (2 cup) حسب قياس الكوب المعياري 240 مل. الدقيق يزن حوالي 125 غرام للكوب الواحد.",
        },
      },
      {
        "@type": "Question",
        name: "كيف أحول السكر من غرام إلى كوب؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "كوب السكر الأبيض العادي يزن 200 غرام تقريباً. لذلك 100 غرام سكر تساوي نصف كوب. السكر البني أثقل قليلاً بسبب الرطوبة، كوب السكر البني المعبأ يزن 220 غرام.",
        },
      },
      {
        "@type": "Question",
        name: "هل يختلف تحويل الغرام إلى كوب بين الدقيق والسكر؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "نعم، يختلف بشكل كبير لأن الكثافة مختلفة. كوب الدقيق أخف بكثير (125 غرام) من كوب السكر (200 غرام). المحول هنا يستخدم كثافات محددة لكل مكون لضمان دقة التحويل.",
        },
      },
    ],
  },
];

// Ingredient densities (grams per cup - based on US cup 240ml)
const INGREDIENT_DENSITIES = {
  flour: { name: "دقيق (عادي)", gramsPerCup: 125 },
  breadFlour: { name: "دقيق الخبز", gramsPerCup: 130 },
  wholeWheatFlour: { name: "دقيق أسمر", gramsPerCup: 120 },
  sugar: { name: "سكر أبيض", gramsPerCup: 200 },
  brownSugar: { name: "سكر بني (معبأ)", gramsPerCup: 220 },
  powderedSugar: { name: "سكر بودرة", gramsPerCup: 120 },
  butter: { name: "زبدة", gramsPerCup: 227 },
  milk: { name: "حليب سائل", gramsPerCup: 240 },
  water: { name: "ماء", gramsPerCup: 240 },
  oil: { name: "زيت نباتي", gramsPerCup: 218 },
  honey: { name: "عسل", gramsPerCup: 340 },
  rice: { name: "أرز (غير مطبوخ)", gramsPerCup: 190 },
  oats: { name: "شوفان", gramsPerCup: 90 },
  cocoa: { name: "كاكاو", gramsPerCup: 100 },
};

function GramsToCupsCalculator() {
  // States
  const [ingredient, setIngredient] = useState("flour");
  const [customDensity, setCustomDensity] = useState("");
  const [grams, setGrams] = useState("");
  const [cups, setCups] = useState("");
  const [unit, setUnit] = useState("grams");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  // Calculate based on grams input
  const calculateFromGrams = () => {
    try {
      if (!grams || parseFloat(grams) <= 0) {
        setError("");
        setResult(null);
        return;
      }

      const gramsValue = parseFloat(grams);

      // Get density
      let gramsPerCup;
      if (ingredient === "custom") {
        gramsPerCup = parseFloat(customDensity);
        if (!gramsPerCup || gramsPerCup <= 0) {
          setError("يرجى إدخال كثافة صحيحة (غرام/كوب)");
          setResult(null);
          return;
        }
      } else {
        gramsPerCup = INGREDIENT_DENSITIES[ingredient].gramsPerCup;
      }

      // Calculate cups
      const cupsValue = gramsValue / gramsPerCup;
      setCups(cupsValue.toFixed(3));

      // Set result object
      setResult({
        grams: gramsValue,
        cups: cupsValue,
        gramsPerCup: gramsPerCup,
        ingredientName: ingredient === "custom" ? "مكون مخصص" : INGREDIENT_DENSITIES[ingredient].name,
        tablespoons: (cupsValue * 16).toFixed(1),
        teaspoons: (cupsValue * 48).toFixed(1),
        milliliters: (cupsValue * 240).toFixed(0),
      });
      setError("");
    } catch (err) {
      setError("خطأ في الحساب");
      setResult(null);
    }
  };

  // Calculate based on cups input
  const calculateFromCups = () => {
    try {
      if (!cups || parseFloat(cups) <= 0) {
        setError("");
        setResult(null);
        return;
      }

      const cupsValue = parseFloat(cups);

      // Get density
      let gramsPerCup;
      if (ingredient === "custom") {
        gramsPerCup = parseFloat(customDensity);
        if (!gramsPerCup || gramsPerCup <= 0) {
          setError("يرجى إدخال كثافة صحيحة (غرام/كوب)");
          setResult(null);
          return;
        }
      } else {
        gramsPerCup = INGREDIENT_DENSITIES[ingredient].gramsPerCup;
      }

      // Calculate grams
      const gramsValue = cupsValue * gramsPerCup;
      setGrams(gramsValue.toFixed(1));

      // Set result object
      setResult({
        grams: gramsValue,
        cups: cupsValue,
        gramsPerCup: gramsPerCup,
        ingredientName: ingredient === "custom" ? "مكون مخصص" : INGREDIENT_DENSITIES[ingredient].name,
        tablespoons: (cupsValue * 16).toFixed(1),
        teaspoons: (cupsValue * 48).toFixed(1),
        milliliters: (cupsValue * 240).toFixed(0),
      });
      setError("");
    } catch (err) {
      setError("خطأ في الحساب");
      setResult(null);
    }
  };

  // Handle grams input change
  const handleGramsChange = (e) => {
    const value = e.target.value;
    setGrams(value);
    setUnit("grams");
    if (value) {
      calculateFromGrams();
    } else {
      setResult(null);
    }
  };

  // Handle cups input change
  const handleCupsChange = (e) => {
    const value = e.target.value;
    setCups(value);
    setUnit("cups");
    if (value) {
      calculateFromCups();
    } else {
      setResult(null);
    }
  };

  // Recalculate when ingredient changes
  useEffect(() => {
    if (unit === "grams" && grams) {
      calculateFromGrams();
    } else if (unit === "cups" && cups) {
      calculateFromCups();
    }
  }, [ingredient, customDensity]);

  const handleClear = () => {
    setGrams("");
    setCups("");
    setUnit("grams");
    setResult(null);
    setError("");
    setCustomDensity("");
  };

  return (
    <div className="rtl" style={{ direction: "rtl" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "5px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>محول من غرام إلى كوب</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            {/* Introduction */}
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>محول الغرام إلى كوب</strong> أداة ذكية لتحويل المقادير في الوصفات. 
                نواجه كلنا مشكلة تحويل الغرام إلى أكواب، خصوصاً بالمكونات المختلفة. 
                هذا المحول يحسب لك الكمية بدقة حسب نوع المكون.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />
            {/* Calculator Section */}
            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>
                  حول بسهولة بين الغرام والكوب
                </h2>
              </div>

              {/* Ingredient Selection */}
              <div className="row">
                <div className="col-sm-12">
                  <label htmlFor="ingredient" >
                    نوع المكون
                  </label>
                  <select
                    id="ingredient"
                    className="form-select"
                    value={ingredient}
                    onChange={(e) => setIngredient(e.target.value)}
                  >
                    {Object.entries(INGREDIENT_DENSITIES).map(([key, { name, gramsPerCup }]) => (
                      <option key={key} value={key}>
                        {name} ({gramsPerCup} غرام/كوب)
                      </option>
                    ))}
                    <option value="custom">كثافة مخصصة</option>
                  </select>
                </div>
              </div>

              {/* Custom Density */}
              {ingredient === "custom" && (
                <div className="row mt-2">
                  <div className="col-sm-12">
                    <label htmlFor="customDensity" >
                      الكثافة (غرام/كوب)
                    </label>
                    <input
                      id="customDensity"
                      type="number"
                      className="form-control"
                      value={customDensity}
                      onChange={(e) => setCustomDensity(e.target.value)}
                      min="0"
                      step="1"
                      placeholder="مثلاً: 125"
                      style={{ backgroundColor: "#fff3cd" }}
                    />
                  </div>
                </div>
              )}

              {/* Input Fields */}
              <div className="row mt-3">
                <div className="col-sm-12 col-md-6">
                  <label htmlFor="grams" >
                    الكمية بالغرام (g)
                  </label>
                  <input
                    id="grams"
                    type="number"
                    className="form-control"
                    value={grams}
                    onChange={handleGramsChange}
                    min="0"
                    step="1"
                    placeholder="أدخل الكمية بالغرام"
                    style={{ backgroundColor: unit === "grams" && grams ? "#e8f5e9" : "" }}
                  />
                </div>
                <div className="col-sm-12 col-md-6">
                  <label htmlFor="cups" >
                    الكمية بالكوب (cup)
                  </label>
                  <input
                    id="cups"
                    type="number"
                    className="form-control"
                    value={cups}
                    onChange={handleCupsChange}
                    min="0"
                    step="0.01"
                    placeholder="أدخل الكمية بالكوب"
                    style={{ backgroundColor: unit === "cups" && cups ? "#e8f5e9" : "" }}
                  />
                </div>
              </div>

              {/* Buttons */}
              <div className="text-center mb-3 mt-4">
                <button
                  onClick={handleClear}
                  className="btn btn-secondary"
                  style={{
                    fontSize: "16px",
                    padding: "10px 30px",
                  }}
                >
                  امسح الكل
                </button>
              </div>

              {/* Error Message */}
              {error && (
                <div className="alert alert-warning" role="alert">
                  <strong>تنبيه:</strong> {error}
                </div>
              )}

              {/* Results */}
              {result && (
                <div className="mt-4">
                  <h3
                    style={{
                      fontSize: "18px",
                      marginBottom: "15px",
                      textAlign: "center",
                    }}
                  >
                    <strong>النتيجة:</strong>
                  </h3>
                  {/* Main Results Table */}
                  <div className="mb-3">
                    <table className="table bmi-table" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th className="bmi-header" style={{ textAlign: "center" }}>
                            البيان
                          </th>
                          <th className="bmi-header" style={{ textAlign: "center" }}>
                            القيمة
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ textAlign: "center" }}>
                            {result.grams.toFixed(1)} غرام من {result.ingredientName}
                          </td>
                          <td
                            style={{
                              textAlign: "center",
                              fontSize: "18px",
                              fontWeight: "bold",
                              color: "#1a5f3f",
                            }}
                          >
                            = {result.cups.toFixed(3)} كوب
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>
                            {result.cups.toFixed(3)} كوب من {result.ingredientName}
                          </td>
                          <td
                            style={{
                              textAlign: "center",
                              fontSize: "18px",
                              fontWeight: "bold",
                              color: "#0f3d28",
                            }}
                          >
                            = {result.grams.toFixed(1)} غرام
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="info-sections-container">
                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>تحويلات إضافية</strong>
                      </div>
                      <div className="info-section-body">
                        <ul style={{ listStyle: "none", padding: 0, lineHeight: "2" }}>
                          <li>
                            🥄 <strong>ملاعق طعام:</strong> {result.tablespoons} ملعقة كبيرة
                          </li>
                          <li>
                            🥄 <strong>ملاعق شاي:</strong> {result.teaspoons} ملعقة صغيرة
                          </li>
                          <li>
                            🥛 <strong>مليلتر:</strong> {result.milliliters} مل
                          </li>
                        </ul>
                        <div
                          className="alert alert-info mt-3"
                          style={{ fontSize: "14px" }}
                        >
                          <strong>💡 معلومة:</strong> الكثافة المستخدمة: {result.gramsPerCup} غرام/كوب
                        </div>
                      </div>
                    </div>
                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>تفاصيل الحساب</strong>
                      </div>
                      <div className="info-section-body">
                        <div style={{ lineHeight: "1.8" }}>
                          <p>
                            <strong>المكون:</strong> {result.ingredientName}
                          </p>
                          <p>
                            <strong>كثافة المكون:</strong> {result.gramsPerCup} غرام لكل كوب
                          </p>
                          <hr />
                          <p style={{ fontSize: "14px", color: "#666" }}>
                            <strong>معادلة التحويل:</strong>
                          </p>
                          <p style={{ fontSize: "14px", color: "#666" }}>
                            عدد الأكواب = الغرام ÷ الكثافة (غرام/كوب)
                            <br />
                            {result.grams.toFixed(1)} ÷ {result.gramsPerCup} = {result.cups.toFixed(3)} كوب
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <hr />
            {/* Related Links */}
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>
      {/* Informational Content */}
      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <strong>ما هي حاسبة تحويل الغرام إلى كوب؟</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  أداة ذكية تحل مشكلة يومية بكل مطبخ: تحويل المقادير من الغرام (الوزن) إلى الكوب (الحجم). 
                  مو كل المكونات متساوية! كوب طحين مو نفس كوب سكر بالوزن. 
                  هنا تدخل الكمية وتختار المكون وتشوف النتيجة فوراً.
                </p>
                <p style={{ lineHeight: "1.8", marginTop: "10px" }}>
                  <strong>كيف تستخدمها؟</strong>
                </p>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>اختار نوع المكون من القائمة (دقيق، سكر، زبدة...).</li>
                  <li>إذا مكونك مو موجود، اختار "كثافة مخصصة" وحط القيمة.</li>
                  <li>اكتب الكمية بالغرام أو بالكوب - الحقل الثاني رح يتغير تلقائياً.</li>
                  <li>تحت رح تشوف التحويلات لملاعق الطعام والملاعق الصغيرة.</li>
                </ol>
                <p style={{ lineHeight: "1.8", marginTop: "10px" }}>
                  <strong>مثال:</strong> تبي تعرف كم كوب في 150 غرام زبدة؟ 
                  اختار "زبدة"، اكتب 150 غرام، والنتيجة: 0.66 كوب (تقريباً ثلثين الكوب).
                </p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>استخدامات المحول في المطبخ</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  للخبازين المنزليين:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>وصفات أجنبية:</strong> كثير وصفات تستخدم الغرام وأنت ما عندك ميزان؟ حولها لأكواب.</li>
                  <li><strong>تعديل الوصفات:</strong> تبي تعمل نص الوصفة؟ حول الكميات بسهولة.</li>
                  <li><strong>الدقيق بأنواعه:</strong> دقيق أبيض، أسمر، خبز - كل واحد له وزن مختلف بالكوب.</li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  للطبخ اليومي:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>الأرز والبرغل:</strong> تعرف كم كوب أرز تحتاج لـ 500 غرام.</li>
                  <li><strong>السوائل:</strong> تحويل الحليب والماء والزيت بكل دقة.</li>
                  <li><strong>المكونات الجافة:</strong> الشوفان، الكاكاو، جوز الهند المبشور.</li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  في النظام الغذائي:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>حساب السعرات:</strong> تحول وزن الأكل لكوب عشان تطبيقات التغذية.</li>
                  <li><strong>وصفات صحية:</strong> كثير مدونين صحة يستخدموا الكوب مقياس.</li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>جدول كثافات المكونات الشائعة</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  هل تعلم أن كوب العسل أثقل من كوب الزيت بمرتين؟ السبب هو الكثافة. 
                  الجدول يوضح كم غرام في الكوب لكل مكون (الكوب = 240 مل).
                </p>
                <table className="table bmi-table" style={{ marginTop: "1rem" }}>
                  <thead>
                    <tr>
                      <th className="bmi-header" style={{ textAlign: "center" }}>المكون</th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>غرام/كوب</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td style={{ textAlign: "center" }}>دقيق (عادي)</td><td style={{ textAlign: "center" }}>125</td></tr>
                    <tr><td style={{ textAlign: "center" }}>دقيق الخبز</td><td style={{ textAlign: "center" }}>130</td></tr>
                    <tr><td style={{ textAlign: "center" }}>دقيق أسمر</td><td style={{ textAlign: "center" }}>120</td></tr>
                    <tr><td style={{ textAlign: "center" }}>سكر أبيض</td><td style={{ textAlign: "center" }}>200</td></tr>
                    <tr><td style={{ textAlign: "center" }}>سكر بني (معبأ)</td><td style={{ textAlign: "center" }}>220</td></tr>
                    <tr><td style={{ textAlign: "center" }}>سكر بودرة</td><td style={{ textAlign: "center" }}>120</td></tr>
                    <tr><td style={{ textAlign: "center" }}>زبدة</td><td style={{ textAlign: "center" }}>227</td></tr>
                    <tr><td style={{ textAlign: "center" }}>ماء / حليب</td><td style={{ textAlign: "center" }}>240</td></tr>
                    <tr><td style={{ textAlign: "center" }}>زيت نباتي</td><td style={{ textAlign: "center" }}>218</td></tr>
                    <tr><td style={{ textAlign: "center" }}>عسل</td><td style={{ textAlign: "center" }}>340</td></tr>
                    <tr><td style={{ textAlign: "center" }}>أرز (نيء)</td><td style={{ textAlign: "center" }}>190</td></tr>
                    <tr><td style={{ textAlign: "center" }}>شوفان</td><td style={{ textAlign: "center" }}>90</td></tr>
                  </tbody>
                </table>
                <div className="alert alert-success mt-3" style={{ lineHeight: "1.8" }}>
                  <strong>✓ فائدة:</strong> المكونات الخفيفة مثل الشوفان والدقيق كتلتها قليلة بالكوب، 
                  بينما الثقيلة مثل العسل والزبدة وزنها أكبر.
                </div>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>أمثلة عملية من مطبخك</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  مثال 1: كيكة الفانيليا
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>الوصفة الأصلية:</strong> 250 غرام دقيق، 200 غرام سكر، 4 بيضات.
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>التحويل:</strong> 
                </p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>250 غرام دقيق = 250 ÷ 125 = 2 كوب دقيق بالضبط.</li>
                  <li>200 غرام سكر = 200 ÷ 200 = 1 كوب سكر.</li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  مثال 2: معمول بالتمر
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>الكمية:</strong> 500 غرام طحين، 300 غرام زبدة.
                </p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>طحين: 500 ÷ 125 = 4 أكواب.</li>
                  <li>زبدة: 300 ÷ 227 = 1.32 كوب (تقريباً كوب وثلث).</li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  مثال 3: شوفان بالفواكه
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>الكمية:</strong> 150 غرام شوفان، كوب حليب.
                </p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>شوفان: 150 ÷ 90 = 1.67 كوب (كوب وثلثين تقريباً).</li>
                </ul>
                <div className="alert alert-info mt-3" style={{ fontSize: "14px" }}>
                  <strong>💡 نصيحة:</strong> إذا بتضاعف الوصفة أو تنصفها، حول أولاً ثم اضرب.
                </div>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>نصائح للتحويل الدقيق</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  نصائح عامة:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>استخدم كوب معياري:</strong> كل الأكواب مو نفس الحجم. الكوب المعياري = 240 مل.</li>
                  <li><strong>الدقيق مهم:</strong> إذا كان الدقيق منخول أو مضغوط، وزنه يتغير. استخدم الدقيق العادي غير المنخول للدقة.</li>
                  <li><strong>السكر البني:</strong> دايم "معبأ" بالكوب (اضغط عليه شوي بالكوب) عشان الكثافة تكون صحيحة.</li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  أخطاء شائعة:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>❌ استخدام نفس القياس لكل المكونات:</strong> كوب سكر مو نفس كوب دقيق!</li>
                  <li><strong>❌ تجاهل نوع الدقيق:</strong> دقيق الخبز أثقل من العادي.</li>
                  <li><strong>❌ عدم التفريق بين السائل والجاف:</strong> المكونات السائلة تقاس بشكل مختلف.</li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  للنتيجة المثالية:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>إذا الوصفة بالمكونات الجافة، الأفضل استخدام ميزان إذا كان متوفر. لكن هذا المحول دقيق جداً للاستخدام اليومي.</li>
                  <li>جرب الوصفة مرة وحدة بالقيم المحولة، وراجع النتيجة. بتضبط معك!</li>
                </ul>
                <div className="alert alert-warning mt-3" style={{ lineHeight: "1.8" }}>
                  <strong>⚠️ ملاحظة:</strong> التحويلات للأكياس أو المكونات المعبأة مسبقاً قد تختلف قليلاً حسب الشركة المصنعة.
                </div>
              </div>
            </div>
          </div>
          <br />
          <h2 style={{ fontSize: "20px", marginBottom: "15px" }}>
            أسئلة متكررة
          </h2>
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>كم كوب يساوي 100 غرام دقيق؟</h3>
              </div>
              <div className="info-section-body">
                100 غرام دقيق عادي = 0.8 كوب. يعني أقل من كوب بشوي. بالضبط، الكوب الكامل من الدقيق وزنه 125 غرام. 
                فـ 100 غرام تعادل ⅘ الكوب. إذا استخدمت دقيق الخبز، 100 غرام = 0.77 كوب لأنه أثقل شوي.
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>كم غرام في كوب السكر الأبيض؟</h3>
              </div>
              <div className="info-section-body">
                كوب السكر الأبيض العادي = 200 غرام. إذا عندك وصفة تطلب كوبين سكر، وزنها 400 غرام. 
                مهم تعرف هالشي إذا كنت تستخدم ميزان. السكر حبيباته ثقيلة مقارنة بالدقيق.
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>هل يختلف تحويل الزبدة عن باقي المكونات؟</h3>
              </div>
              <div className="info-section-body">
                نعم، الزبدة حالة خاصة. كوب الزبدة = 227 غرام (حوالي 225 غرام في أغلب المراجع). 
                سهل تتعامل معها: لوح الزبدة الصغير (القالب) غالباً 100 غرام، وهو تقريباً أقل من نص كوب بشوي.
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>كيف أحول 500 غرام طحين لأكواب؟</h3>
              </div>
              <div className="info-section-body">
                500 غرام طحين ÷ 125 (كثافة الطحين) = 4 أكواب بالضبط. سهل، صح؟ 
                إذا كان الطحين منخول، الكثافة تقل شوي (تصير 115 تقريباً) فتحتاج 4.3 كوب.
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>ما الفرق بين كوب السكر البني والأبيض؟</h3>
              </div>
              <div className="info-section-body">
                السكر البني يحتوي على دبس،所以他 أثقل. كوب السكر البني المعبأ = 220 غرام. 
                بينما الأبيض = 200 غرام. بالخبز، هذا الفرق بسيط لكن بالمقادير الكبيرة يفرق.
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>هل ينفع أحول العسل بنفس طريقة السكر؟</h3>
              </div>
              <div className="info-section-body">
                لا أبداً! العسل كثافته عالية جداً. كوب العسل = 340 غرام، بينما السكر 200 غرام. 
                لو حطيت كوب عسل بدل كوب سكر بالوصفة، النتيجة رح تكون كارثة. دايم استخدم المحول الخاص.
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>كم كوب يساوي 250 غرام أرز؟</h3>
              </div>
              <div className="info-section-body">
                250 غرام أرز غير مطبوخ = 1.3 كوب تقريباً (كوب وثلث). 
                الأرز كثافته 190 غرام/كوب. إذا طبخت الأرز، بتلاحظ أن حجمه يتضاعف 3 مرات!
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>ميزاني مو متوفر، هل المحول دقيق؟</h3>
              </div>
              <div className="info-section-body">
                المحول دقيق جداً إذا استخدمت كوب معياري (240 مل). الأبحاث تقول أن الأكواب المنزلية تختلف كثيراً، 
                فحاول تشتري كوب قياس رخيص من السوبرماركت. ومع الممارسة، رح تضبط المقادير بالعين!
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GramsToCupsCalculator;